import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  useTheme,
  alpha,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  SvgIcon
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';
import PetsIcon from '@mui/icons-material/Pets';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';

// Import from our business service
import { 
  getBusiness, 
  getBadges,
  getAllBadges
} from '../services/business';

import { 
  Business,
  Badge
} from '../services/business';

interface AchievementsProps {
  businessId: string;
}

const Achievements = ({ businessId }: AchievementsProps) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [badges, setBadges] = useState<Badge[]>([]);
  const [allBadges, setAllBadges] = useState<Record<string, Badge>>({});
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [badgeHtml, setBadgeHtml] = useState('');
  const [copied, setCopied] = useState(false);
  const badgeHtmlRef = useRef<HTMLDivElement>(null);
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch business info
        const businessData = await getBusiness(businessId);
        
        // Fetch earned badges
        const badgesData = await getBadges(businessId);
        setBadges(badgesData);
        
        // Fetch all available badges
        const allBadgesData = await getAllBadges();
        setAllBadges(allBadgesData);
        
        setError('');
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error loading data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [businessId]);

  // Handle opening share dialog
  const handleOpenShareDialog = (badge: Badge) => {
    setSelectedBadge(badge);
    setShareDialogOpen(true);
    
    // Create the embed code when opening the dialog
    const embedCode = `<div style="display:inline-block;background-color:${alpha(badge.color, 0.1)};padding:12px;border-radius:8px;border:1px solid ${alpha(badge.color, 0.3)};font-family:sans-serif;">
  <div style="display:flex;align-items:center;">
    <span style="font-size:24px;margin-right:8px;">${badge.icon}</span>
    <span style="font-weight:bold;color:${badge.color};">${badge.title}</span>
  </div>
  <div style="margin-top:8px;font-size:12px;">Awarded by OCPA Green P<span style="font-weight:800;">100</span>neers</div>
</div>`;
    
    setBadgeHtml(embedCode);
    setCopied(false);
  };

  // Handle closing share dialog
  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setSelectedBadge(null);
  };

  // Copy embed code to clipboard
  const handleCopyEmbedCode = () => {
    if (!selectedBadge) return;
    
    const embedCode = `<div style="display:inline-block;background-color:${alpha(selectedBadge.color, 0.1)};padding:12px;border-radius:8px;border:1px solid ${alpha(selectedBadge.color, 0.3)};font-family:sans-serif;">
  <div style="display:flex;align-items:center;">
    <span style="font-size:24px;margin-right:8px;">${selectedBadge.icon}</span>
    <span style="font-weight:bold;color:${selectedBadge.color};">${selectedBadge.title}</span>
  </div>
  <div style="margin-top:8px;font-size:12px;">Awarded by OCPA Green P<span style="font-weight:800;">100</span>neers</div>
</div>`;
    
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setBadgeHtml(embedCode);
    
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  // Get locked badges (all badges minus earned badges)
  const lockedBadges = Object.values(allBadges).filter(
    badge => !badges.some(earned => earned.title === badge.title)
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Your Green Achievements
          <Chip 
            label="Beta" 
            size="small" 
            sx={{ 
              ml: 2, 
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.main,
              fontWeight: 500,
              border: `1px solid ${theme.palette.warning.main}`
            }} 
          />
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your sustainability progress and showcase your green business credentials.
        </Typography>
      </Box>
      
      {/* Earned Badges Section */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <EmojiEventsIcon sx={{ color: theme.palette.warning.main, mr: 1.5, fontSize: '1.75rem' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Your Earned Badges
          </Typography>
        </Box>
        
        {badges.length > 0 ? (
          <Grid container spacing={3}>
            {badges.map((badge, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    borderRadius: 4,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      height: 8, 
                      bgcolor: badge.color 
                    }} 
                  />
                  <CardContent sx={{ p: 3, pt: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography
                        variant="h2"
                        sx={{
                          color: badge.color,
                          mb: 1,
                        }}
                      >
                        {badge.icon}
                      </Typography>
                      <Button 
                        startIcon={<ShareIcon />} 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleOpenShareDialog(badge)}
                        sx={{ 
                          borderColor: alpha(badge.color, 0.5),
                          color: badge.color,
                          '&:hover': {
                            borderColor: badge.color,
                            backgroundColor: alpha(badge.color, 0.1),
                          }
                        }}
                      >
                        Share
                      </Button>
                    </Box>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                      {badge.title}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      {badge.description}
                    </Typography>
                    
                    <Box sx={{ 
                      p: 1.5, 
                      bgcolor: alpha(badge.color, 0.1), 
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: badge.color }}>
                        {badge.criteria}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              p: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              No Achievements Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              As you implement sustainability measures and improve your energy usage, you'll earn badges to showcase your commitment to environmental responsibility.
            </Typography>
          </Card>
        )}
      </Box>
      
      {/* Locked Badges Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <LockIcon sx={{ color: theme.palette.grey[600], mr: 1.5, fontSize: '1.75rem' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Badges to Earn
          </Typography>
        </Box>
        
        <Grid container spacing={3}>
          {lockedBadges.map((badge, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                elevation={0} 
                sx={{ 
                  borderRadius: 4,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  filter: 'grayscale(1)',
                  opacity: 0.7,
                }}
              >
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    height: 8, 
                    bgcolor: theme.palette.grey[500]
                  }} 
                />
                <CardContent sx={{ p: 3, pt: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography
                      variant="h2"
                      sx={{
                        color: theme.palette.grey[500],
                        mb: 1,
                      }}
                    >
                      {badge.icon}
                    </Typography>
                    <LockIcon sx={{ color: theme.palette.grey[500] }} />
                  </Box>
                  
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {badge.title}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {badge.description}
                  </Typography>
                  
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: alpha(theme.palette.grey[500], 0.1), 
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                      {badge.criteria}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      
      {/* Info box */}
      <Box sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          About Achievements
        </Typography>
        <Typography variant="body2" paragraph>
          OCPA Green P<Box component="span" sx={{ fontWeight: 800 }}>100</Box>neers badges recognize your business's commitment to sustainability and renewable energy. Each badge represents a specific achievement in your sustainability journey.
        </Typography>
        <Typography variant="body2">
          Share your badges on your website and social media to showcase your commitment to environmental responsibility and inspire others to follow your lead.
        </Typography>
      </Box>
      
      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={handleCloseShareDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Share Your Achievement
        </DialogTitle>
        <DialogContent>
          {selectedBadge && (
            <>
              <Box sx={{ mb: 3, p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box 
                  sx={{ 
                    p: 3, 
                    bgcolor: alpha(selectedBadge.color, 0.1),
                    borderRadius: 2,
                    border: `1px solid ${alpha(selectedBadge.color, 0.3)}`,
                    textAlign: 'center',
                    maxWidth: '320px'
                  }}
                >
                  <Typography variant="h3" sx={{ color: selectedBadge.color, mb: 1 }}>
                    {selectedBadge.icon}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {selectedBadge.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedBadge.shareText}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                    Awarded by OCPA Green P<Box component="span" sx={{ fontWeight: 800, display: 'inline' }}>100</Box>neers
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Share on Social Media
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <Button 
                  variant="contained" 
                  startIcon={<FacebookIcon />}
                  sx={{ 
                    bgcolor: '#4267B2',
                    '&:hover': {
                      bgcolor: '#365899',
                    }
                  }}
                >
                  Facebook
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={<LinkedInIcon />}
                  sx={{ 
                    bgcolor: '#0A66C2',
                    '&:hover': {
                      bgcolor: '#084a8e',
                    }
                  }}
                >
                  LinkedIn
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={<InstagramIcon />}
                  sx={{ 
                    background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #e6843a 0%, #d65f38 25%, #c7233b 50%, #b71f5b 75%, #a51577 100%)',
                    }
                  }}
                >
                  Instagram
                </Button>
              </Box>
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Embed on Your Website
              </Typography>
              
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={badgeHtml}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                        <IconButton onClick={handleCopyEmbedCode} edge="end">
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Box>
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Direct Link
              </Typography>
              
              <TextField
                fullWidth
                value={`https://green-pioneers.ocpa.com/badge/${selectedBadge.title.toLowerCase().replace(/\s+/g, '-')}`}
                InputProps={{
                  readOnly: true,
                  startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                variant="outlined"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Achievements; 